/**
 * Korrekturen im Scan-Dialog (T-84): Jeder erkannte Wert lässt sich vor der
 * Übernahme berichtigen — bei allen Dokumentarten.
 *
 * Die Korrektur liegt als Überlagerung über dem Server-Befund. Damit Mapper,
 * Firmensuche, Gegenseite und Übernahme ohne Änderung mit dem korrigierten
 * Wert arbeiten, wird der eingegebene Text hier in die Form des Befunds
 * zurückübersetzt (Beträge als Zahl, Führerscheinklassen als Liste).
 *
 * Eine Korrektur läuft nicht mehr durch die Prüfungen des Servers
 * (`repariereEmail`, Rohtext-Abgleich) — deshalb eine Mindestprüfung, damit
 * nichts Ungültiges ins Formular gelangt.
 */
import { jahreseinkommenAusEingabe } from "./steuerUndAusweisAngaben";

type Befund = Record<string, unknown>;

/** Beträge der Gehaltsabrechnung — der Befund trägt sie als Zahl. */
const BETRAG_FELDER = new Set([
  "gesamtbruttoMonat",
  "steuerbruttoMonat",
  "svBruttoMonat",
  "nettoAuszahlung",
  "gesamtbruttoJahr",
  "steuerbruttoJahr",
]);
const EMAIL_FELDER = new Set(["email", "emailZentrale", "emailPrivat"]);
const NUMMER_FELDER = new Set(["telefon", "mobil", "fax", "telefonZentrale", "telefonPrivat", "mobilPrivat"]);
const LISTEN_FELDER = new Set(["klassen"]);

export type KorrekturTyp = "betrag" | "email" | "nummer" | "liste" | "datum" | "text";

/** Wie ein Feld eingegeben und zurückübersetzt wird. */
export function korrekturTyp(feld: string, art: string | null | undefined): KorrekturTyp {
  if (art === "gehaltsabrechnung" && BETRAG_FELDER.has(feld)) return "betrag";
  if (EMAIL_FELDER.has(feld)) return "email";
  if (NUMMER_FELDER.has(feld)) return "nummer";
  if (LISTEN_FELDER.has(feld)) return "liste";
  if (/datum$/i.test(feld) || feld === "gueltigBis") return "datum";
  return "text";
}

/** `inputMode` des Eingabefelds je Typ. */
export function korrekturInputMode(typ: KorrekturTyp): "email" | "tel" | "decimal" | "text" {
  return typ === "email" ? "email" : typ === "nummer" ? "tel" : typ === "betrag" ? "decimal" : "text";
}

/**
 * Text → Befundwert. `undefined` heißt: das Feld ist geleert und fällt aus
 * dem Befund (es erscheint dann nicht mehr in der Prüfliste).
 */
export function korrekturWert(feld: string, text: string, art: string | null | undefined): unknown {
  const roh = (text ?? "").trim();
  if (!roh) return undefined;
  switch (korrekturTyp(feld, art)) {
    case "betrag":
      return jahreseinkommenAusEingabe(roh) ?? undefined;
    case "email":
      return roh.toLowerCase();
    case "liste": {
      const teile = roh
        .split(/[,;\s]+/)
        .map((t) => t.trim())
        .filter(Boolean);
      return teile.length ? teile : undefined;
    }
    default:
      return roh;
  }
}

/** Einen korrigierten Wert in den Befund schreiben (oder ihn entfernen). */
export function korrekturAnwenden(befund: Befund, feld: string, text: string, art: string | null | undefined): Befund {
  return korrekturenUeberlagern(befund, { [feld]: korrekturWert(feld, text, art) });
}

/**
 * Alle Korrekturen über den Befund legen. Ein Schlüssel mit `undefined`
 * löscht das Feld. Von Hand geänderte Felder verlieren die Markierung der
 * MRZ-Prüfziffer — die gilt nur für den gelesenen Wert.
 */
export function korrekturenUeberlagern(befund: Befund, korrekturen: Record<string, unknown>): Befund {
  const schluessel = Object.keys(korrekturen);
  if (!schluessel.length) return befund;
  const neu: Befund = { ...befund };
  for (const feld of schluessel) {
    const wert = korrekturen[feld];
    if (wert === undefined) delete neu[feld];
    else neu[feld] = wert;
  }
  if (Array.isArray(befund.geprueft)) {
    neu.geprueft = schluessel.reduce<string[]>((liste, feld) => geprueftOhne({ geprueft: liste }, feld), [
      ...(befund.geprueft as string[]),
    ]);
  }
  return neu;
}

/** Die Prüfziffern-Liste ohne dieses Feld. */
export function geprueftOhne(befund: Befund, feld: string): string[] {
  const liste = Array.isArray(befund.geprueft) ? (befund.geprueft as string[]) : [];
  return liste.filter((f) => f !== feld);
}

const EMAIL_MUSTER = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const DATUM_DE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;
const DATUM_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

const gueltigesDatum = (t: number, m: number, j: number): boolean => {
  const d = new Date(Date.UTC(j, m - 1, t));
  return d.getUTCFullYear() === j && d.getUTCMonth() === m - 1 && d.getUTCDate() === t;
};

/**
 * Mindestprüfung einer Eingabe. Liefert die Meldung für die Zeile oder
 * `null`, wenn der Wert übernommen werden kann. Ein leerer Text ist gültig —
 * er nimmt den Wert heraus.
 */
export function korrekturPruefen(feld: string, text: string, art: string | null | undefined): string | null {
  const roh = (text ?? "").trim();
  if (!roh) return null;
  switch (korrekturTyp(feld, art)) {
    case "email":
      return EMAIL_MUSTER.test(roh) ? null : "Keine gültige E-Mail-Adresse.";
    case "nummer":
      return (roh.match(/\d/g)?.length ?? 0) >= 6 ? null : "Eine Rufnummer braucht mindestens sechs Ziffern.";
    case "betrag":
      return jahreseinkommenAusEingabe(roh) === null || !/\d/.test(roh)
        ? "Bitte einen Betrag eingeben, z. B. 3.600,50."
        : null;
    case "datum": {
      const de = DATUM_DE.exec(roh);
      if (de && gueltigesDatum(Number(de[1]), Number(de[2]), Number(de[3]))) return null;
      const iso = DATUM_ISO.exec(roh);
      if (iso && gueltigesDatum(Number(iso[3]), Number(iso[2]), Number(iso[1]))) return null;
      return "Bitte als Datum eingeben (TT.MM.JJJJ).";
    }
    default:
      return null;
  }
}
